/**
 * The constraint model.
 *
 * Classic sudoku is not hardcoded anywhere downstream. It is 27 instances of an
 * all-different constraint (9 rows, 9 columns, 9 boxes) produced by the factory
 * below. The solvers, the candidate computation and every solving technique
 * iterate this list instead of assuming rows/columns/boxes exist.
 *
 * That indirection is the whole reason variants stay cheap: Sudoku X adds two
 * more all-different constraints, Jigsaw replaces the box factory, and Killer /
 * Thermo add new `kind`s carrying their own data. None of them require touching
 * the solvers. Retrofitting this later would mean rewriting all of core/.
 */

import { CELLS, SIZE, cellAt } from "./grid";

export type ConstraintKind = "row" | "col" | "box";

export interface Constraint {
  readonly kind: ConstraintKind;
  /** Ordinal within its kind -- row 0-8, col 0-8, box 0-8 (reading order). */
  readonly index: number;
  /** Cells that must all hold different digits. */
  readonly cells: readonly number[];
}

export interface ConstraintSet {
  readonly constraints: readonly Constraint[];
  /** cell -> ids of the constraints containing it. */
  readonly byCell: readonly (readonly number[])[];
  /** cell -> every other cell sharing a constraint with it (20 for classic). */
  readonly peers: readonly (readonly number[])[];
}

function buildConstraintSet(constraints: Constraint[]): ConstraintSet {
  const byCell: number[][] = Array.from({ length: CELLS }, () => []);
  const peerSets: Set<number>[] = Array.from({ length: CELLS }, () => new Set<number>());

  constraints.forEach((constraint, id) => {
    for (const cell of constraint.cells) {
      byCell[cell]!.push(id);
      for (const other of constraint.cells) {
        if (other !== cell) peerSets[cell]!.add(other);
      }
    }
  });

  return {
    constraints,
    byCell,
    peers: peerSets.map((set) => [...set].sort((a, b) => a - b)),
  };
}

export function classicConstraints(): ConstraintSet {
  const constraints: Constraint[] = [];

  for (let r = 0; r < SIZE; r++) {
    const cells: number[] = [];
    for (let c = 0; c < SIZE; c++) cells.push(cellAt(r, c));
    constraints.push({ kind: "row", index: r, cells });
  }

  for (let c = 0; c < SIZE; c++) {
    const cells: number[] = [];
    for (let r = 0; r < SIZE; r++) cells.push(cellAt(r, c));
    constraints.push({ kind: "col", index: c, cells });
  }

  for (let b = 0; b < SIZE; b++) {
    const cells: number[] = [];
    const baseRow = ((b / 3) | 0) * 3;
    const baseCol = (b % 3) * 3;
    for (let dr = 0; dr < 3; dr++) {
      for (let dc = 0; dc < 3; dc++) cells.push(cellAt(baseRow + dr, baseCol + dc));
    }
    constraints.push({ kind: "box", index: b, cells });
  }

  return buildConstraintSet(constraints);
}

/**
 * The default rule set. Built once and shared -- it is immutable, and every
 * consumer takes it as a defaulted parameter so variants can pass their own.
 */
export const CLASSIC: ConstraintSet = classicConstraints();

/** "row 4", "column 7", "box 3" -- how hint text refers to a unit. */
export function unitName(constraint: Constraint): string {
  const noun = constraint.kind === "col" ? "column" : constraint.kind;
  return `${noun} ${constraint.index + 1}`;
}

/** Cells shared by two constraints. */
export function intersection(a: Constraint, b: Constraint): number[] {
  const inB = new Set(b.cells);
  return a.cells.filter((cell) => inB.has(cell));
}

const peerMatrixCache = new WeakMap<ConstraintSet, Uint8Array>();

/**
 * Flat cell-by-cell peer lookup. The wings and chains ask "does A see B?" tens
 * of thousands of times per solve, which is too hot for a linear scan of
 * `peers`.
 */
export function peerMatrix(cs: ConstraintSet): Uint8Array {
  let matrix = peerMatrixCache.get(cs);
  if (matrix) return matrix;

  matrix = new Uint8Array(CELLS * CELLS);
  for (let cell = 0; cell < CELLS; cell++) {
    for (const peer of cs.peers[cell]!) matrix[cell * CELLS + peer] = 1;
  }
  peerMatrixCache.set(cs, matrix);
  return matrix;
}

/** True if the two cells share at least one constraint. */
export function sees(cs: ConstraintSet, a: number, b: number): boolean {
  return peerMatrix(cs)[a * CELLS + b] === 1;
}
